import * as React from "react";
import { useDispatch } from "react-redux";

import makeStyles from "@material-ui/styles/makeStyles";
import MuiDrawer from "@material-ui/core/Drawer";
import Typography from "@material-ui/core/Typography";
import Divider from "@material-ui/core/Divider";
import { fade } from "@material-ui/core/styles/colorManipulator";

import IconButton from "@material-ui/core/IconButton";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import ActionSend from "@material-ui/icons/Send";
import ActionClose from "@material-ui/icons/Close";

import { Button, showNotification } from "jazasoft";

import MuiTable from "../../../components/MuiTable";

import { getDistinctValues, isEmpty, toFixedValue } from "../../../utils/helpers";
import { useDeepMemo, useMutation } from "../../../hooks";
import { isKnitRoll } from "../../../helpers/rollReturnHelper";
import ConfirmButton from "../../../components/ConfirmButton";

const CONFIRM_COLUMN_WIDTHS = ["20%", "11%", "7.5%", "7.5%", "15%", "15%", "18%"];

const useStyles = makeStyles(theme => ({
  drawerPaper: {
    maxWidth: 1000
  },
  drawerHeader: {
    display: "flex",
    width: 1000,
    alignItems: "center",
    padding: theme.spacing(0, 1),
    ...theme.mixins.toolbar,
    justifyContent: "flex-start"
  },
  content: {
    display: "flex",
    flex: 1,
    flexDirection: "column",
    padding: "1.5em"
  },
  marginTop: {
    marginTop: "1.5em"
  },
  totalRow: {
    display: "grid",
    gridTemplateColumns: CONFIRM_COLUMN_WIDTHS.join(" "),
    borderTop: "1px solid rgba(224, 224, 224, 1)",
    backgroundColor: "#fafafa",
    fontWeight: 600,
    whiteSpace: "nowrap",
    width: "100%"
  },
  totalCell: {
    padding: "8px",
    boxSizing: "border-box",
    overflow: "hidden",
    textOverflow: "ellipsis"
  },
  submitButton: {
    backgroundColor: theme.palette.primary.main,
    color: "white",
    "&:hover": {
      backgroundColor: fade(theme.palette.primary.main, 0.7),
      // Reset on mouse devices
      "@media (hover: none)": {
        backgroundColor: "transparent"
      }
    }
  }
}));

const RollReturnConfirmDrawer = props => {
  const classes = useStyles();
  const { open, cutOrderId, selectedRolls = [], onDrawerClose, onRefresh } = props;

  const [mutate, { loading: saving }] = useMutation();
  const dispatch = useDispatch();
  const contentRef = React.useRef(null);

  const isKnit = selectedRolls.some(isKnitRoll);

  const sumRows = React.useCallback((rows, dataKey) => rows.reduce((total, row) => total + (Number(row[dataKey]) || 0), 0), []);

  const rows = useDeepMemo(
    () =>
      selectedRolls.map(roll => ({
        ...roll,
        confirmQty: isKnit ? roll.weight : roll.length,
        remarks: ""
      })),
    [selectedRolls, isKnit]
  );

  const [confirmQtyValues, setConfirmQtyValues] = React.useState([]);

  React.useEffect(() => {
    setConfirmQtyValues(rows.map(row => (isKnit ? row.weight : row.length)));
  }, [open, rows, isKnit]);

  const syncConfirmQtyValues = React.useCallback(() => {
    const inputs = contentRef.current?.querySelectorAll('input[id$="confirmQty-text-input"]') || [];
    setConfirmQtyValues(Array.from(inputs, input => input.value));
  }, []);

  const columns = useDeepMemo(
    () =>
      [
        { dataKey: "rollNo", title: "F. Roll No." },
        { dataKey: "supplierRollNo", title: "S. Roll No." },
        { dataKey: "groupName", title: "Group" },
        { dataKey: "shade", title: "Shade" },
        isKnit
          ? { dataKey: "weight", title: "Return Qty\n by Cutting", render: value => `${toFixedValue(value, 3)} kg` }
          : { dataKey: "length", title: "Return Qty\n by Cutting", render: value => `${toFixedValue(value)} m` },
        {
          dataKey: "confirmQty",
          title: "Return \nConfirmation Qty",
          inputType: "text-input",
          options: { type: "number", onChange: syncConfirmQtyValues, onInput: syncConfirmQtyValues }
        },
        { dataKey: "remarks", title: "Remarks", inputType: "text-input" }
      ].map((column, index) => ({
        ...column,
        headerCellProps: { style: { width: CONFIRM_COLUMN_WIDTHS[index] } },
        rowCellProps: { style: { width: CONFIRM_COLUMN_WIDTHS[index] } }
      })),
    [isKnit, syncConfirmQtyValues]
  );

  const returnQtyDataKey = isKnit ? "weight" : "length";
  const returnQtyTotal = sumRows(rows, returnQtyDataKey);
  const confirmQtyTotal = confirmQtyValues.reduce((total, value) => total + (Number(value) || 0), 0);

  const totalRowCells = [
    "Total",
    " ",
    " ",
    " ",
    `${toFixedValue(returnQtyTotal, isKnit ? 3 : 2)} ${isKnit ? "kg" : "m"}`,
    `${toFixedValue(confirmQtyTotal, isKnit ? 3 : 2)} ${isKnit ? "kg" : "m"}`,
    " "
  ];

  const onSubmit = (editedRows, _, onSubmitComplete) => {
    if (isEmpty(editedRows)) return;

    const rollReturnIds = getDistinctValues(editedRows.map(r => r.rollReturnId));
    if (rollReturnIds?.length > 1) {
      dispatch(showNotification("Only one Return transaction can be confirmed at a time.", { type: "danger" }));
      return;
    }

    const options = {
      url: `roll-return/${rollReturnIds[0]}/confirm`,
      method: "put",
      params: { cutOrderId },
      data: {
        id: rollReturnIds[0],
        rollList: editedRows.map(roll => ({
          id: roll.id,
          rollNo: roll.rollNo,
          ...(isKnit ? { returnedWeight: Number(roll.confirmQty) } : { returnedLength: Number(roll.confirmQty) }),
          storeRemarks: roll.remarks
        }))
      }
    };
    mutate(options, {
      onSuccess: () => {
        dispatch(showNotification("Roll return confirmed successfully."));
        onRefresh && onRefresh();
        onDrawerClose && onDrawerClose();
      },
      onFailure: () => onDrawerClose && onDrawerClose()
    });
  };

  let ffHandleSubmit;
  const handleSubmitRef = submit => {
    ffHandleSubmit = submit;
  };

  return (
    <MuiDrawer classes={{ paper: classes.drawerPaper }} anchor="right" open={open} onClose={onDrawerClose}>
      <div className={classes.drawerHeader}>
        <IconButton onClick={onDrawerClose}>
          <ChevronRightIcon />
        </IconButton>
        <div>
          <Typography variant="h5">Roll Return Confirmation</Typography>
        </div>
      </div>
      <Divider />

      <div ref={contentRef} className={classes.content}>
        <MuiTable
          key={open}
          columns={columns}
          rows={rows}
          idKey="id"
          parentIdKey="none"
          tableProps={{ style: { tableLayout: "fixed", width: "100%" } }}
          editable
          editing
          onSubmit={onSubmit}
          handleSubmitRef={handleSubmitRef}
        />

        <div className={classes.totalRow}>
          {totalRowCells.map((value, index) => (
            <div className={classes.totalCell} key={index}>
              {value}
            </div>
          ))}
        </div>

        <div style={{ display: "flex", flex: 1, flexGrow: 1 }} />

        <div className={classes.marginTop}>
          <ConfirmButton
            className={classes.submitButton}
            label="Submit"
            // color="primary"
            icon={<ActionSend />}
            confirmTitle="Are you sure?"
            confirmMessage={"This step is irreversible. Make sure you have received these rolls from Cutting Floor."}
            disabled={saving || isEmpty(selectedRolls)}
            onConfirm={event => ffHandleSubmit(event)}
          />

          <Button label="Cancel" variant="outlined" color="primary" onClick={onDrawerClose} style={{ marginLeft: "1em" }}>
            <ActionClose />
          </Button>
        </div>
      </div>
    </MuiDrawer>
  );
};

export default RollReturnConfirmDrawer;
